import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type DiffuseMode = "normal" | "anisotropic";

export interface DiffuseParams {
  mode: DiffuseMode;
  intensity: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Diffuse: Filter<DiffuseParams> = {
  id: "diffuse",
  displayName: "Diffuse",
  defaultParams: { mode: "normal", intensity: 50 },

  apply(input: PixelBuffer, params: DiffuseParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const intensity = params.intensity / 100;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;

        if (params.mode === "anisotropic") {
          const lum = src[o]! * 0.299 + src[o + 1]! * 0.587 + src[o + 2]! * 0.114;
          const angle = (lum / 255) * Math.PI;
          const sx = Math.round(x + Math.cos(angle) * intensity * 3);
          const sy = Math.round(y + Math.sin(angle) * intensity * 3);
          if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
            const si = (sy * w + sx) * 4;
            dst[o] = src[si]!; dst[o + 1] = src[si + 1]!; dst[o + 2] = src[si + 2]!; dst[o + 3] = src[si + 3]!;
          } else {
            dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
          }
        } else {
          const dx = Math.round((Math.random() - 0.5) * intensity * 6);
          const dy = Math.round((Math.random() - 0.5) * intensity * 6);
          const sx = Math.max(0, Math.min(w - 1, x + dx));
          const sy = Math.max(0, Math.min(h - 1, y + dy));
          const si = (sy * w + sx) * 4;
          dst[o] = src[si]!; dst[o + 1] = src[si + 1]!; dst[o + 2] = src[si + 2]!; dst[o + 3] = src[si + 3]!;
        }
      }
    }
    return out;
  },
};
